/*
 |--------------------------------------------------------------------------
 | Collapsible
 |
 | This module adds functionality to the Sitecore version of
 | the collapsible component
 |--------------------------------------------------------------------------
 */

// Constants
const ACollapsibleSelector = '.a-collapsible';
const ACollapsibleTriggerSelector = '.a-collapsible__trigger';
const ACollapsibleOpenAttribute = 'open';
const ACollapsibleStartOpenAttribute = 'a-collapsible-start-open';
const ACollapsibleOpenIconSelector = '.a-collapsible__open-indicator';
const ACollapsibleCloseIconSelector = '.a-collapsible__close-indicator';

// Class
class ACollapsible {
  constructor(element) {
    this.element = element;
    this.trigger = this.element?.querySelector(ACollapsibleTriggerSelector);
    this.openIndicator = this.trigger?.querySelector(
      ACollapsibleOpenIconSelector
    );
    this.closeIndicator = this.trigger?.querySelector(
      ACollapsibleCloseIconSelector
    );

    const targetId = this.trigger?.getAttribute('aria-controls');
    this.target = targetId ? document.querySelector(`#${targetId}`) : null;
  }

  init = () => {
    if (!this.element || !this.trigger || !this.target) {
      return;
    }

    if (
      !this.element.hasAttribute(ACollapsibleStartOpenAttribute) ||
      this.element.getAttribute(ACollapsibleStartOpenAttribute) === 'false'
    ) {
      this.hide();
    } else {
      this.show();
    }

    this.trigger.addEventListener('click', this.toggle);
  };

  toggle = () => {
    if (this.isVisible()) {
      this.hide();
    } else {
      this.show();
    }
  };

  isVisible = () => {
    return (
      this.target.hasAttribute(ACollapsibleOpenAttribute) &&
      this.trigger.getAttribute('aria-expanded') === 'true'
    );
  };

  show = () => {
    this.target.setAttribute(ACollapsibleOpenAttribute, '');
    this.trigger.setAttribute('aria-expanded', 'true');

    if (this.openIndicator && this.closeIndicator) {
      this.closeIndicator.removeAttribute('hidden');
      this.openIndicator.setAttribute('hidden', 'true');
    }

    this.element.dispatchEvent(new Event('collapsibleOpened'));
  };

  hide = () => {
    this.target.removeAttribute(ACollapsibleOpenAttribute);
    this.trigger.setAttribute('aria-expanded', 'false');

    if (this.openIndicator && this.closeIndicator) {
      this.openIndicator.removeAttribute('hidden');
      this.closeIndicator.setAttribute('hidden', 'true');
    }

    this.element.dispatchEvent(new Event('collapsibleClosed'));
  };
}

// Global open function
window.aOpenCollapsible = (collapsibleId) => {
  const collapsible = document.getElementById(collapsibleId);
  if (collapsible === null) {
    throw new Error('No collapsible found with id="' + collapsibleId + '".');
  }

  collapsible.collapsibleClass.show();
};

// Global close  function
window.aCloseCollapsible = (collapsibleId) => {
  const collapsible = document.getElementById(collapsibleId);
  if (collapsible === null) {
    throw new Error('No collapsible found with id="' + collapsibleId + '".');
  }

  collapsible.collapsibleClass.hide();
};

// Initiate module
function aCollapsibleCreateInstance(element) {
  if (!element.collapsibleClass) {
    element.collapsibleClass = new ACollapsible(element);
    element.collapsibleClass.init();
  }
}
document
  .querySelectorAll(`${ACollapsibleSelector}`)
  .forEach(aCollapsibleCreateInstance);

// Create new Collapsible instances on DOM mutation
const ACollapsibleObserver = new MutationObserver(function (mutations) {
  mutations.forEach((mutation) => {
    mutation.addedNodes.forEach((node) => {
      if (node.nodeType !== Node.ELEMENT_NODE) {
        return;
      }

      // Node is a collapsible
      if (node.matches && node.matches(ACollapsibleSelector)) {
        aCollapsibleCreateInstance(node);
      }

      // Node contains any collapsibles
      if (node.querySelectorAll) {
        node
          .querySelectorAll(ACollapsibleSelector)
          .forEach(aCollapsibleCreateInstance);
      }
    });
  });
});

ACollapsibleObserver.observe(document, {
  attributes: false,
  childList: true,
  characterData: false,
  subtree: true,
});
